import { cn } from '@/utils/cn';
import type { ButtonHTMLAttributes } from 'react';
import { Loader } from './Loader';

type Variant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
type Size = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  fullWidth?: boolean;
  loading?: boolean;
  loadingText?: string;
}

const variants: Record<Variant, string> = {
  primary: 'bg-primary text-white hover:bg-primary-600 shadow-sm',
  secondary: 'bg-secondary text-white hover:bg-secondary-600 shadow-sm',
  outline: 'border border-gray-200 bg-white text-brand-navy hover:bg-brand-surface',
  ghost: 'text-brand-navy hover:bg-brand-surface',
  danger: 'bg-red-500 text-white hover:bg-red-600',
};

const sizes: Record<Size, string> = {
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-5 py-2.5 text-sm font-semibold',
  lg: 'px-6 py-3 text-base font-semibold',
};

const loaderVariant: Record<Variant, 'white' | 'brand' | 'muted'> = {
  primary: 'white',
  secondary: 'white',
  outline: 'brand',
  ghost: 'brand',
  danger: 'white',
};

export function Button({
  className,
  variant = 'primary',
  size = 'md',
  fullWidth,
  loading = false,
  loadingText,
  disabled,
  children,
  ...props
}: ButtonProps) {
  const isDisabled = disabled || loading;

  return (
    <button
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-xl transition-colors focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:cursor-not-allowed disabled:opacity-60',
        variants[variant],
        sizes[size],
        fullWidth && 'w-full',
        className,
      )}
      disabled={isDisabled}
      aria-busy={loading}
      {...props}
    >
      {loading && <Loader size="sm" variant={loaderVariant[variant]} label={loadingText || 'Loading'} />}
      {loading ? loadingText || children : children}
    </button>
  );
}
